import { lazy } from 'react'
import WidgetCategory from '../../WidgetCategory'
import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import { parseHtmlConfigFields, toConfigDescriptions } from './html-config'
import { CodeWidgetHtmlHelp } from './code-widget-html-help'
import { AlertCircle } from 'lucide-react'
import { SimpleTooltip } from '../../ui-primitives/simple-tooltip'

/**
 * Config variables for the CodeWidget. Kept `as const` so the prop types can be
 * derived from it (see CodeWidgetConfigOptions).
 */
export const codeWidgetOptions = [
  {
    name: 'query',
    type: 'yasgui',
    label:
      'SPARQL SELECT query. Its JSON results arrive as this.props.data and are ' +
      'exposed to the page as window.wistor.data (and window.WIDGET_DATA).',
  },
  {
    name: 'allowedRuleTags',
    type: 'text',
    label: 'comma separated ruletags code widget is allowed to call'
  },
  {
    name: 'html',
    type: 'html',
    label: (
      <>
        A complete standalone HTML page. It runs in a sandboxed iframe, reads the
        SPARQL results from window.wistor.data, and can write back with
        window.wistor.updateQuads({"{ variant: 'strict-v1', inserts: [...triples] }"}).
        <CodeWidgetDisclaimer />
      </>
    ),
    helpComponent: CodeWidgetHtmlHelp,
  },
  {
    name: 'config',
    type: 'json',
    label:
      'Optional configuration object, exposed to the page as window.wistor.config ' +
      '(and window.WIDGET_CONFIG), e.g. { "layout": "LR" }',
  },
  {
    name: 'minHeight',
    type: 'text',
    label:
      'Minimum height of the iframe as a CSS length (e.g. "640px", "40vh"). ' +
      'Leave empty to let the iframe fill its area without a minimum.',
  },
  {
    formGroup: 'Changelog configuration',
    name: 'mutationLogsEnabled',
    type: 'boolean',
    label: 'enable edit-logs to triples',
  },
  {
    formGroup: 'Changelog configuration',
    name: 'mutationLogsGraph',
    type: 'text',
    label: 'graph to write edit-logs in',
    topicValueInterpolationEnabled: true,
  },
  {
    formGroup: 'Changelog configuration',
    name: 'mutationLogsCustomStatements',
    type: 'text',
    label: 'custom statements for edit-logs',
    topicValueInterpolationEnabled: true,
  },
  {
    name: 'targetGraph',
    type: 'text',
    label:
      'Default named graph IRI for updateQuads. Used for triples the page sends ' +
      'without an explicit graph (a per-update graph or a TriG named graph overrides it).',
  },
] as const

export type RawCodeWidgetConfigOptions = ConfigurablePropsFromDescription<typeof codeWidgetOptions>

export type CodeWidgetConfigOptions = RawCodeWidgetConfigOptions

export const codeWidgetDescription = {
  name: 'Code Widget',
  component: lazy(() => import('./code-widget')),
  label: 'Code Widget',
  category: WidgetCategory.experimental,
  variables: codeWidgetOptions,
  /*
   * Self-describing config: the config dialog renders extra fields declared by
   * the current page HTML (see html-config). Stored under the "configField:" prefix.
   */
  deriveExtraVariables: (config: Record<string, unknown>) =>
    toConfigDescriptions(parseHtmlConfigFields(typeof config.html === 'string' ? config.html : null)),
}

function CodeWidgetDisclaimer() {
  return (
    <SimpleTooltip tooltip={
      <span className="block max-w-[360px] text-left">
        Disclaimer: Code created or run in the Code widget is executed at the user’s own risk.
        Wistor applies multiple layers of protection, including a sandboxed environment with
        restricted access to libraries and communication, backend data security. However, Wistor
        cannot guarantee that generated or user-supplied code is error-free, secure, or fit for
        any particular purpose.
      </span>
    }>
      <button
        type="button"
        className="mt-2 flex cursor-help items-center gap-1 border-none bg-transparent p-0 text-inherit"
      >
        <AlertCircle size={14} />
        Disclaimer
      </button>
    </SimpleTooltip>
  )
}
